  import { useState, useEffect, useMemo, useCallback } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useSensorData } from '../context/SensorDataContext'
import { iotControllerApi, assetApi } from '../api/services'
import { PlusIcon, SearchIcon } from '../components/IoTControllers/Icons'
import ControllerCard from '../components/IoTControllers/ControllerCard'
import ControllerModal from '../components/IoTControllers/ControllerModal'
import AssetMapModal from '../components/IoTControllers/AssetMapModal'
import DeleteModal from '../components/IoTControllers/DeleteModal'
import ParamMappingModal from '../components/IoTControllers/ParamMappingModal'

// ── Role-based access constants ──────────────────────────────────────────────
const CAN_WRITE_ROLES = ['super_admin', 'admin']
const CAN_VIEW_ROLES  = ['super_admin', 'admin', 'operator', 'viewer']

const IoTControllers = () => {
  const { user, activeTenant } = useAuth()
  const resolvedTenant =
    (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ??
    user?.tenant_id

  const isSuperAdmin = user?.role === 'super_admin'
  const canWrite = CAN_WRITE_ROLES.includes(user?.role)
  const canView  = CAN_VIEW_ROLES.includes(user?.role)
  const {
    livePayloads,
    loadHistoricalData,
    clearLivePayload,
    clearLiveData,
    fetchLivePayload
  } = useSensorData()

  const [controllers, setControllers] = useState([])
  const [assets, setAssets] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  const [showAddModal, setShowAddModal] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [mapTarget, setMapTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [paramTarget, setParamTarget] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!resolvedTenant) return
    setIsLoading(true)
    try {
      const [ctrlRes, assetRes] = await Promise.all([
        iotControllerApi.getAll(resolvedTenant),
        assetApi.getAll({ tenantId: resolvedTenant }),
      ])

      const list = ctrlRes.data?.data || ctrlRes.data || []
      setControllers(list)
      loadHistoricalData(list)

      const assetData = assetRes.data?.data || assetRes.data || []
      setAssets(Array.isArray(assetData) ? assetData : assetData.items || [])
    } catch {
      toast.error('Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }, [resolvedTenant, loadHistoricalData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Resolve the current status for a controller, preferring live Socket.IO data
  const getStatus = useCallback(
    ctrl => {
      const s = (livePayloads[ctrl.id]?.status || ctrl.status || 'offline').toLowerCase()
      return s === 'offline' ? 'offline' : 'online'
    },
    [livePayloads]
  )

  const counts = useMemo(
    () => ({
      all: controllers.length,
      online: controllers.filter(c => getStatus(c) === 'online').length,
      offline: controllers.filter(c => getStatus(c) === 'offline').length
    }),
    [controllers, getStatus]
  )

  const filtered = useMemo(
    () =>
      controllers.filter(c => {
        const matchTab = activeTab === 'all' || getStatus(c) === activeTab
        const matchSearch = (c.name || '').toLowerCase().includes(searchQuery.toLowerCase())
        return matchTab && matchSearch
      }),
    [controllers, activeTab, searchQuery, getStatus]
  )

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const handleSave = async payload => {
    setIsSaving(true)
    try {
      if (editTarget) {
        const res = await iotControllerApi.update(
          editTarget.id,
          payload,
          resolvedTenant
        )
        const updated = res.data?.data || res.data
        setControllers(prev =>
          prev.map(c =>
            c.id === editTarget.id
              ? {
                  ...c,
                  ...updated
                }
              : c
          )
        )

        // immediate refresh for status on update: clear real-time cache so socket state re-evaluates
        clearLivePayload(updated.id)
        clearLiveData(editTarget?.device_id)
        if (updated?.device_id) {
          clearLiveData(updated.device_id)
        }

        // Force check for live payload after location/asset change
        fetchLivePayload(updated.id, resolvedTenant)

        toast.success('Controller updated')
      } else {
        const res = await iotControllerApi.create(payload, resolvedTenant)
        const created = res.data?.data || res.data
        setControllers(prev => [...prev, created])
        toast.success('Controller created')
      }
      setShowAddModal(false)
      setEditTarget(null)
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to save controller'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleAssignAsset = async assetIds => {
    setIsSaving(true)
    try {
      const res = await iotControllerApi.assignAsset(
        mapTarget.id,
        assetIds,
        resolvedTenant
      )
      const updated = res.data?.data || res.data
      setControllers(prev =>
        prev.map(c =>
          c.id === mapTarget.id
            ? {
                ...c,
                ...updated
              }
            : c
        )
      )

      clearLivePayload(mapTarget.id)
      clearLiveData(mapTarget?.device_id)
      if (updated?.device_id) {
        clearLiveData(updated.device_id)
      }

      // Force check for live payload after asset assignment
      fetchLivePayload(mapTarget.id, resolvedTenant)

      toast.success('Assets assigned')
      setMapTarget(null)
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to assign assets'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    setIsSaving(true)
    try {
      await iotControllerApi.delete(deleteTarget.id, resolvedTenant)
      setControllers(prev => prev.filter(c => c.id !== deleteTarget.id))
      toast.success('Controller deleted')
      setDeleteTarget(null)
    } catch {
      toast.error('Failed to delete controller')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleActive = async ctrl => {
    const next = ctrl.is_active === false // currently inactive → activating
    // Optimistic update
    setControllers(prev =>
      prev.map(c => (c.id === ctrl.id ? { ...c, is_active: next } : c))
    )
    try {
      const res = await iotControllerApi.setActive(ctrl.id, next, resolvedTenant)
      const updated = res.data?.data || res.data
      setControllers(prev =>
        prev.map(c => (c.id === ctrl.id ? { ...c, ...updated } : c))
      )
      // Drop any cached live data so a deactivated card stops showing readings
      clearLivePayload(ctrl.id)
      clearLiveData(ctrl?.device_id)
      toast.success(next ? 'Controller activated' : 'Controller deactivated')
    } catch (err) {
      // Surface backend message (e.g. "asset 'X' is inactive — activate it first")
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to update controller status'
      )
      // Revert on failure
      setControllers(prev =>
        prev.map(c => (c.id === ctrl.id ? { ...c, is_active: !next } : c))
      )
    }
  }

  const TABS = [
    { id: 'all',     label: `All (${counts.all})`        },
    { id: 'online',  label: `Online (${counts.online})`   },
    { id: 'offline', label: `Offline (${counts.offline})` }
  ]

  if (isLoading)
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 text-gray-500'>
        Loading controllers…
      </div>
    )

  return (
    <div className='bg-gray-50 min-h-screen p-8 font-sans'>
      <Toaster position='top-right' />

      {/* Header */}
      <div className='flex items-start justify-between mb-6'>
        <div>
          <div className='flex items-center gap-3'>
            <h1 className='text-2xl font-bold text-gray-800'>
              IoT Controller Management
            </h1>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
              isSuperAdmin
                ? 'bg-purple-50 text-purple-700 border-purple-200'
                : canWrite
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-gray-50 text-gray-600 border-gray-200'
            }`}>
              {isSuperAdmin ? 'Super Admin — All Tenants' : canWrite ? 'Admin' : 'Read-Only'}
            </span>
          </div>
          <p className='text-sm text-gray-500 mt-1'>
            {isSuperAdmin
              ? 'Viewing controllers across the selected organisation'
              : canWrite
              ? 'Onboard and manage IoT controllers'
              : 'View-only access to IoT controllers'}
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => {
              setEditTarget(null)
              setShowAddModal(true)
            }}
            className='flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white px-5 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-colors'
          >
            <PlusIcon /> Add Controller
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6 flex items-center justify-between gap-4 flex-wrap'>
        <div className='flex items-center gap-1'>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-800 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className='relative'>
          <span className='absolute left-3 top-1/2 -translate-y-1/2'>
            <SearchIcon />
          </span>
          <input
            type='text'
            placeholder='Search by name'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className='pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 w-64'
          />
        </div>
      </div>

      {/* Card grid */}
      {filtered.length === 0 ? (
        <div className='text-center py-16 text-gray-400'>
          <p className='text-base font-medium'>No controllers found</p>
          <p className='text-sm mt-1'>
            Try a different filter or add a new controller above
          </p>
        </div>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
          {filtered.map(ctrl => (
            <ControllerCard
              key={ctrl.id}
              controller={ctrl}
              canWrite={canWrite}
              livePayload={livePayloads[ctrl.id] || null}
              onEdit={c => {
                setEditTarget(c)
                setShowAddModal(true)
              }}
              onDelete={setDeleteTarget}
              onSetParams={setParamTarget}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <ControllerModal
          initial={editTarget}
          assets={assets}
          tenantPublicId={user?.tenant_public_id}
          onSave={handleSave}
          onClose={() => {
            setShowAddModal(false)
            setEditTarget(null)
          }}
          isSaving={isSaving}
        />
      )}
      {mapTarget && (
        <AssetMapModal
          controller={mapTarget}
          assets={assets}
          onAssignAsset={handleAssignAsset}
          onClose={() => setMapTarget(null)}
          isSaving={isSaving}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          controller={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          isSaving={isSaving}
        />
      )}
      {paramTarget && (
        <ParamMappingModal
          controller={paramTarget}
          tenantId={resolvedTenant}
          livePayload={livePayloads[paramTarget.id] || null}
          onClose={() => setParamTarget(null)}
        />
      )}
    </div>
  )
}

export default IoTControllers
